import React, { useState, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Upload, X, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { getAccessToken } from '@/lib/api';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

/**
 * Shared image / file uploader. Accepts URL paste OR file upload via the
 * existing /api/uploads endpoint. Returns the resulting URL via onChange.
 *
 * Props:
 *  - value: current URL
 *  - onChange: (url) => void
 *  - kind: folder bucket (default 'menu')
 *  - allowPdf: enable PDF uploads (for driver IDs etc.)
 *  - placeholder: input placeholder
 *  - previewHeight: tailwind h-* class for preview area (default 'h-24')
 */
export function ImageUploader({
  value,
  onChange,
  kind = 'menu',
  allowPdf = false,
  placeholder = 'https://… or upload →',
  previewHeight = 'h-24',
  testIdPrefix = 'image-upload',
}) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const acceptStr = allowPdf
    ? 'image/jpeg,image/png,image/webp,image/gif,application/pdf'
    : 'image/jpeg,image/png,image/webp,image/gif';

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = getAccessToken();
      const res = await fetch(`${API}/uploads?kind=${encodeURIComponent(kind)}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.detail || `Upload failed (HTTP ${res.status})`);
      }
      const data = await res.json();
      onChange(data.url);
      toast.success('Uploaded');
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const fullUrl = value?.startsWith('/api')
    ? `${API.replace(/\/api$/, '')}${value}`
    : value;
  const isPdf = (value || '').toLowerCase().endsWith('.pdf');

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input
          data-testid={`${testIdPrefix}-url-input`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1"
        />
        <input
          ref={fileRef}
          type="file"
          accept={acceptStr}
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
          data-testid={`${testIdPrefix}-file-input`}
        />
        <Button
          data-testid={`${testIdPrefix}-trigger`}
          type="button"
          size="sm"
          variant="secondary"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          <Upload className="mr-1 h-4 w-4" /> {uploading ? 'Uploading…' : 'Upload'}
        </Button>
      </div>
      {value && (
        <div className="relative inline-block overflow-hidden rounded-md border border-border">
          {isPdf ? (
            <a href={fullUrl} target="_blank" rel="noreferrer" className={`flex ${previewHeight} w-40 items-center justify-center gap-2 bg-muted text-sm font-medium text-foreground hover:bg-muted/70`}>
              <FileText className="h-4 w-4" /> View PDF
            </a>
          ) : (
            <img src={fullUrl} alt="preview" className={`${previewHeight} w-40 object-cover`} />
          )}
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="absolute right-1 top-1 h-6 w-6 rounded-full bg-black/40 text-white hover:bg-black/60"
            onClick={() => onChange('')}
            data-testid={`${testIdPrefix}-clear`}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}
      <p className="text-[10px] text-muted-foreground">
        {allowPdf ? 'JPEG/PNG/WebP/GIF/PDF up to 10 MB.' : 'JPEG/PNG/WebP/GIF up to 10 MB.'}
      </p>
    </div>
  );
}

export default ImageUploader;
